import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";

const BRAND = {
  orange: "#F35A3A",
  orangeSoft: "#FF9B7E",
  ink: "#F7F8FA",
  muted: "rgba(247,248,250,.64)",
  panel: "#111820",
  panelSoft: "#151E27",
};

const heroStories = [
  {
    eyebrow: "Tu vidriera digital",
    title: "Una página web que explica tu negocio y te ayuda a vender.",
    text: "Publicá tu negocio, tus servicios o tus productos en una página web pensada para celular y conectada con las herramientas que usás para trabajar.",
    icon: LanguageRoundedIcon,
    metric: "Página + catálogo",
    detail: "Todo se edita desde WebProfit",
    items: ["Página web editable", "Catálogo actualizado", "WhatsApp y contacto"],
  },
  {
    eyebrow: "Operación conectada",
    title: "Stock, ventas y caja en el mismo lugar.",
    text: "Dejá de reconstruir el negocio entre planillas. Registrá lo que pasa y revisá stock, movimientos y dinero desde el panel.",
    icon: PointOfSaleRoundedIcon,
    metric: "Gestión diaria",
    detail: "También desde el celular",
    items: ["Stock y movimientos", "Caja y cuentas", "Ventas registradas"],
  },
  {
    eyebrow: "Negocios con agenda",
    title: "Servicios, clientes y turnos ordenados.",
    text: "Mostrá qué hacés, publicá servicios y organizá la disponibilidad sin mezclar mensajes, agenda y seguimiento.",
    icon: EventAvailableRoundedIcon,
    metric: "Agenda conectada",
    detail: "Servicios y clientes",
    items: ["Servicios públicos", "Reservas y turnos", "Historial de clientes"],
  },
  {
    eyebrow: "Equipo y crecimiento",
    title: "Más personas operando, sin perder control.",
    text: "Sumá accesos y permisos para que cada persona vea lo que necesita y el negocio siga teniendo una sola fuente de verdad.",
    icon: Groups2RoundedIcon,
    metric: "Equipo conectado",
    detail: "Accesos y control",
    items: ["Accesos por rol", "Cobros y pedidos", "Actividad y seguimiento"],
  },
];

const capabilityPills = [
  { icon: Inventory2OutlinedIcon, label: "Stock" },
  { icon: PaymentsRoundedIcon, label: "Cobros" },
  { icon: EventAvailableRoundedIcon, label: "Turnos" },
  { icon: TrendingUpRoundedIcon, label: "Resultados" },
];

function StoryCard({ story, index, total }) {
  const Icon = story.icon;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: { xs: 382, sm: 430, md: 460 },
        p: { xs: 2, sm: 2.8, md: 3.4 },
        borderRadius: { xs: 4, sm: 5, md: 6 },
        bgcolor: "rgba(17,24,32,.92)",
        color: BRAND.ink,
        border: "1px solid rgba(255,255,255,.1)",
        boxShadow: "0 36px 90px rgba(0,0,0,.38)",
        backdropFilter: "blur(22px)",
        "&::before": {
          content: '\"\"',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 92% 8%, rgba(243,90,58,.22), transparent 32%), linear-gradient(140deg, rgba(255,255,255,.035), transparent 45%)",
          pointerEvents: "none",
        },
      }}
    >
      <Stack sx={{ position: "relative", zIndex: 1, height: "100%" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.15}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                color: "#fff",
                bgcolor: "rgba(243,90,58,.18)",
                border: "1px solid rgba(243,90,58,.24)",
              }}
            >
              <Icon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: ".74rem", letterSpacing: ".1em", textTransform: "uppercase", color: BRAND.orangeSoft }}>
                {story.eyebrow}
              </Typography>
              <Typography sx={{ mt: .2, color: "rgba(255,255,255,.5)", fontSize: ".76rem", fontWeight: 750 }}>
                Vista {index + 1} de {total}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label="Online"
            size="small"
            sx={{ bgcolor: "rgba(45,185,125,.13)", color: "#82DFB0", fontWeight: 900, border: "1px solid rgba(45,185,125,.15)" }}
          />
        </Stack>

        <Typography sx={{ mt: { xs: 2.6, sm: 3.4 }, fontWeight: 950, letterSpacing: "-.042em", lineHeight: 1.04, fontSize: { xs: "1.72rem", sm: "2.2rem", md: "2.48rem" } }}>
          {story.title}
        </Typography>
        <Typography sx={{ mt: 1.25, color: "rgba(255,255,255,.6)", lineHeight: 1.65, fontSize: { xs: ".88rem", sm: ".96rem" } }}>
          {story.text}
        </Typography>

        <Box
          sx={{
            mt: { xs: 2.4, sm: 3.2 },
            p: { xs: 1.5, sm: 2 },
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,.055)",
            border: "1px solid rgba(255,255,255,.07)",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: ".72rem", fontWeight: 850, textTransform: "uppercase", letterSpacing: ".08em" }}>
                {story.metric}
              </Typography>
              <Typography sx={{ mt: .45, color: "#fff", fontWeight: 900, fontSize: { xs: ".96rem", sm: "1.04rem" } }}>
                {story.detail}
              </Typography>
            </Box>
            <CheckCircleRoundedIcon sx={{ color: "#82DFB0" }} />
          </Stack>

          <Box sx={{ mt: 1.55, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)", md: "1fr" }, gap: .8 }}>
            {story.items.map((item) => (
              <Stack key={item} direction="row" spacing={.7} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND.orange, flexShrink: 0 }} />
                <Typography sx={{ color: "rgba(255,255,255,.72)", fontSize: ".79rem", fontWeight: 750 }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function HeroSection({
  onStartClick,
  onExploreClick,
  primaryLabel = "Crear mi página gratis",
  isAuthenticated = false,
  userName = "",
}) {
  const [activeStory, setActiveStory] = React.useState(0);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveStory((current) => (current + 1) % heroStories.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <Box
      component="section"
      aria-labelledby="webprofit-home-title"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 11, sm: 13, md: 16 },
        pb: { xs: 6, sm: 8, md: 12 },
        color: BRAND.ink,
        bgcolor: "#0B1016",
        background:
          "radial-gradient(circle at 82% 18%, rgba(243,90,58,.19), transparent 28%), radial-gradient(circle at 12% 78%, rgba(255,126,87,.09), transparent 30%), linear-gradient(180deg, #090E13 0%, #0B1118 100%)",
        "@keyframes homeGlowFloat": {
          "0%, 100%": { transform: "translate3d(0,0,0) scale(1)" },
          "50%": { transform: "translate3d(-34px,24px,0) scale(1.08)" },
        },
        "@keyframes homeGridDrift": {
          "0%": { transform: "translateY(0)" },
          "100%": { transform: "translateY(36px)" },
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          width: { xs: 320, md: 560 },
          height: { xs: 320, md: 560 },
          right: { xs: -190, md: -160 },
          top: { xs: 40, md: 20 },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(243,90,58,.33), rgba(243,90,58,0) 68%)",
          filter: "blur(10px)",
          animation: "homeGlowFloat 9s ease-in-out infinite",
          pointerEvents: "none",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          opacity: .23,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px)",
          backgroundSize: "46px 46px",
          maskImage: "linear-gradient(to bottom, rgba(0,0,0,.78), transparent 88%)",
          animation: "homeGridDrift 12s linear infinite",
          pointerEvents: "none",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.02fr) minmax(410px, .98fr)" },
            alignItems: "center",
            gap: { xs: 4.5, sm: 5.5, md: 7, lg: 9 },
          }}
        >
          <Box sx={{ maxWidth: 720 }}>
            <Chip
              icon={<CheckCircleRoundedIcon />}
              label={isAuthenticated ? `Sesión activa${userName ? ` · ${userName}` : ""}` : "Una sola plataforma para hacer visible y operar tu negocio"}
              sx={{
                mb: { xs: 1.8, sm: 2.5 },
                maxWidth: "100%",
                minHeight: 36,
                height: "auto",
                py: .35,
                borderRadius: 999,
                bgcolor: "rgba(243,90,58,.12)",
                color: "#FFB19A",
                border: "1px solid rgba(243,90,58,.16)",
                fontWeight: 900,
                "& .MuiChip-icon": { color: BRAND.orange },
                "& .MuiChip-label": { whiteSpace: "normal", py: .45 },
              }}
            />

            <Typography
              id="webprofit-home-title"
              component="h1"
              sx={{
                color: "#F8FAFC",
                fontWeight: 950,
                letterSpacing: "-.058em",
                lineHeight: .98,
                fontSize: { xs: "2.5rem", sm: "3.65rem", md: "4.5rem", lg: "5rem" },
              }}
            >
              Tu negocio online,
              <Box component="span" sx={{ display: "block", color: BRAND.orange }}>
                listo para operar.
              </Box>
            </Typography>

            <Typography sx={{ mt: { xs: 1.8, sm: 2.4, md: 2.8 }, maxWidth: 665, color: BRAND.muted, fontSize: { xs: ".98rem", sm: "1.08rem", md: "1.17rem" }, lineHeight: 1.68, fontWeight: 560 }}>
              WebProfit conecta tu página web con todo lo que necesitás para trabajar el negocio: productos, pedidos, stock, caja, clientes, turnos, cobros y empleados. Empezás por lo que necesitás hoy y agregás herramientas cuando te hacen falta.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: { xs: 2.7, sm: 3.4, md: 4 }, maxWidth: { xs: "100%", sm: 570 } }}>
              <Button
                onClick={onStartClick}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  minHeight: { xs: 52, sm: 58 },
                  px: { xs: 2.7, sm: 3.3 },
                  borderRadius: 999,
                  bgcolor: BRAND.orange,
                  textTransform: "none",
                  fontWeight: 950,
                  fontSize: "1rem",
                  boxShadow: "0 16px 38px rgba(243,90,58,.3)",
                  "&:hover": { bgcolor: "#E64A2A", boxShadow: "0 20px 44px rgba(243,90,58,.38)", transform: "translateY(-1px)" },
                }}
              >
                {primaryLabel}
              </Button>
              <Button
                onClick={onExploreClick}
                variant="outlined"
                size="large"
                sx={{
                  minHeight: { xs: 50, sm: 58 },
                  px: { xs: 2.5, sm: 3 },
                  borderRadius: 999,
                  color: "#F8FAFC",
                  borderColor: "rgba(255,255,255,.2)",
                  bgcolor: "rgba(255,255,255,.025)",
                  textTransform: "none",
                  fontWeight: 850,
                  "&:hover": { borderColor: "rgba(255,255,255,.45)", bgcolor: "rgba(255,255,255,.06)" },
                }}
              >
                Ver qué resuelve en mi negocio
              </Button>
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={{ xs: .85, sm: 1.2 }} sx={{ mt: { xs: 2, sm: 2.6 } }}>
              {capabilityPills.map(({ icon: Icon, label }) => (
                <Stack key={label} direction="row" alignItems="center" spacing={.65} sx={{ px: 1.15, py: .7, borderRadius: 999, bgcolor: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.065)" }}>
                  <Icon sx={{ color: BRAND.orangeSoft, fontSize: 17 }} />
                  <Typography sx={{ color: "rgba(255,255,255,.68)", fontWeight: 780, fontSize: ".8rem" }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box key={activeStory} sx={{ animation: "homeStoryIn .55s ease both", "@keyframes homeStoryIn": { from: { opacity: 0, transform: "translateY(16px) scale(.985)" }, to: { opacity: 1, transform: "translateY(0) scale(1)" } }, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}>
              <StoryCard story={heroStories[activeStory]} index={activeStory} total={heroStories.length} />
            </Box>
            <Stack direction="row" spacing={.8} justifyContent="center" sx={{ mt: 1.6 }}>
              {heroStories.map((story, index) => (
                <Box
                  key={story.title}
                  component="button"
                  type="button"
                  aria-label={`Mostrar ${story.eyebrow}`}
                  aria-pressed={activeStory === index}
                  onClick={() => setActiveStory(index)}
                  sx={{
                    border: 0,
                    p: 0,
                    width: activeStory === index ? 34 : 9,
                    height: 9,
                    borderRadius: 999,
                    bgcolor: activeStory === index ? BRAND.orange : "rgba(255,255,255,.2)",
                    cursor: "pointer",
                    transition: "all .3s ease",
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
